import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_bold_right } from '@repo/icons/index'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'
import { CurrentUser } from '../../model/Common/common.interface'
import { useCurrentUser } from '../../modules/context/UserContext'
import LoginLinkA from '../../modules/i18n/LoginLinkA'
import { n_common, n_content } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import urls from '../../modules/urls'
import { getTeensAppDownLinkByDevice } from '../../modules/utils'
import { SIGN_UP_MODE } from '../../modules/vars'

interface ContentsLoginBannerPropsType {
  w?: number
  bottom?: number | string
  top?: number | string
  loginAfterLink?: string
  isMobile: boolean
  bannerLocation: APP_DOWN_CONTENTS_BANNER_LOCATION
  contentsId: string | string[]
}

/**
 * 앱 배너 다운로드 유도 위치
 * - 어느 페이지에서 해당 컴포넌트가 렌더가 되는지 분별용 enum
 */
export enum APP_DOWN_CONTENTS_BANNER_LOCATION {
  TEENS_LANDING_MATERIAL,
  LANDING_MATERIAL,
}
const ContentsLoginBanner = ({
  w = 750,
  bottom = 0,
  top = 0,
  loginAfterLink = urls.oneOnOne.index,
  isMobile = false,
  bannerLocation,
  contentsId,
  ...props
}: ContentsLoginBannerPropsType) => {
  const { t, locale } = useTranslation()
  const { currentUser } = useCurrentUser()
  const router = useRouter()
  const serviceURL = `ringleplus.com/${locale}/student/landing/contents`

  const APP_DOWN_MESSAGE_CLICK_LINK = {
    TEENS_LANDING_MATERIAL: getTeensAppDownLinkByDevice(),
    LANDING_MATERIAL: `https://ringle.page.link/?link=https://${serviceURL}/material/${contentsId}&apn=com.ringle&ibi=org.reactjs.native.example.Ringle&isi=1189876556&efr=1`,
  }

  const appDownDynamicLink = useMemo(() => {
    switch (bannerLocation) {
      case APP_DOWN_CONTENTS_BANNER_LOCATION.TEENS_LANDING_MATERIAL:
        return APP_DOWN_MESSAGE_CLICK_LINK.TEENS_LANDING_MATERIAL
      case APP_DOWN_CONTENTS_BANNER_LOCATION.LANDING_MATERIAL:
        return APP_DOWN_MESSAGE_CLICK_LINK.LANDING_MATERIAL
    }
  }, [bannerLocation])

  const getBannerOutboundUrl: (currentUser: CurrentUser, href: string) => string = useCallback(
    (currentUser: CurrentUser, href: string) => {
      if (currentUser) {
        const materialId = router.query.materialId
        return materialId ? urls.student.newPortal.materials.detail(materialId) : urls.student.newPortal.home
      } else {
        return href
      }
    },
    [router.asPath]
  )

  return isMobile ? (
    <div style={{ bottom: `${bottom}px`, top: `${top}px` }} className="absolute">
      <div>
        <div className="bg-transparent-gradient h-[462px] w-full px-[20px]"></div>
        <div className="flex w-full flex-col items-center justify-center bg-white">
          <div className="Headline2-24Bd keep-all mb-[24px] flex justify-center px-[16px] text-center">
            {t(n_content.sign_in_to_access)}
          </div>

          <div className="mb-[120px]">
            <LoginLinkA
              mode={SIGN_UP_MODE.MATERIAL_SIGN_UP}
              tutor={false}
              href={urls.student.auth.signUp}
              afterLogin={loginAfterLink}
            >
              <div className="w-[248px]">
                <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.MD} onClick={null}>
                  <DSButton.Label>{t(n_common.btn_sign_up_full)}</DSButton.Label>
                </DSButton>
              </div>
            </LoginLinkA>
            <LoginLinkA
              tutor={false}
              href={getBannerOutboundUrl(currentUser, urls.student.auth.signIn)}
              afterLogin={loginAfterLink}
            >
              <div className="Body2-14Bd mt-[16px] flex cursor-pointer flex-row items-center justify-center text-purple-500">
                {t(n_content.sign_in_to_continue)}
                <QIcon_chevron_bold_right width={16} height={16} />
              </div>
            </LoginLinkA>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div style={{ bottom: `${bottom}px`, top: `${top}px` }} className="absolute mx-auto h-[710px] w-full">
      <div className="bg-transparent-gradient h-[450px] max-w-[1040px]"></div>
      <div className="flex h-[280px] flex-col items-center justify-center bg-white">
        <div className="Headline2-24Bd mb-[24px] flex justify-center text-center">{t(n_content.sign_in_to_access)}</div>
        <div className="mb-[120px]">
          <LoginLinkA
            mode={SIGN_UP_MODE.MATERIAL_SIGN_UP}
            tutor={false}
            href={urls.student.auth.signUp}
            afterLogin={loginAfterLink}
          >
            <div className="w-[248px]">
              <DSButton type={BUTTON_TYPE.SOLID} size={BUTTON_SIZE.MD} onClick={null}>
                <DSButton.Label>{t(n_common.btn_sign_up_full)}</DSButton.Label>
              </DSButton>
            </div>
          </LoginLinkA>

          <LoginLinkA
            tutor={false}
            href={getBannerOutboundUrl(currentUser, urls.student.auth.signIn)}
            afterLogin={loginAfterLink}
          >
            <div className="Body2-14Bd mt-[16px] flex cursor-pointer flex-row items-center justify-center text-purple-500">
              {t(n_content.sign_in_to_continue)}
              <QIcon_chevron_bold_right width={16} height={16} />
            </div>
          </LoginLinkA>
        </div>
      </div>
    </div>
  )
}

export default ContentsLoginBanner
